/**
 * overlays.js — boundary / condensation / cloud-area path builders.
 *
 * Pure functions (interpolateSeries, cumulusColumns) are unit-tested.
 * d3 appenders (drawBoundaryLine, drawCondensationLine, drawCloudArea) are
 * covered by the renderer smoke test (Task 10) + visual pass (Task 13).
 *
 * All d3 appenders share the same signature:
 *   (sel, data, yScale, width, gt) → void
 * where:
 *   sel    — d3 selection of the parent <svg> element
 *   data   — parsed windgram data array (the .data array from the API response),
 *             each element has { dt, pblh, zsfclcl, ... }
 *   yScale — d3 scale: altitude (metres) → pixel y
 *   width  — inner chart pixel width
 *   gt     — horizontal scale factor (0.33 for 13-step day, 0.68 for 28-step, 1 otherwise)
 *
 * The caller (renderer.js) computes gt as:
 *   gt = data.length === 28 ? 0.68 : data.length === 13 ? 0.33 : 1
 * and passes MARGIN.left / MARGIN.top as the translate origin.
 */

import { line, area, curveCatmullRom } from 'd3-shape';

// ─── margin constants mirroring MARGIN from constants.js ────────────────────
const MARGIN_LEFT = 45;
const MARGIN_TOP = 5;

// ─── Pure helpers ────────────────────────────────────────────────────────────

/**
 * interpolateSeries — port of bundle `C` (line 1348-1370).
 *
 * For each consecutive pair in `points`, insert `subdivisions` evenly-spaced
 * intermediate points (linear height interpolation). The final original point
 * is appended at the end.
 *
 * @param {Array<{date: string, height: number}>} points
 *   Date strings in 'YYYYMMDDHHmm' format.
 * @param {number} subdivisions  Number of sub-intervals per gap (bundle uses 5).
 * @returns {Array<{date: string, height: number}>}
 */
export function interpolateSeries(points, subdivisions) {
  const out = [];
  for (let i = 0; i < points.length - 1; i++) {
    const cur = points[i];
    const nxt = points[i + 1];
    for (let j = 0; j < subdivisions; j++) {
      const frac = j / subdivisions;
      // Build a proper Date then convert back to ISO string (mirrors bundle)
      const dt = new Date(
        parseInt(cur.date.slice(0, 4), 10),
        parseInt(cur.date.slice(4, 6), 10) - 1,
        parseInt(cur.date.slice(6, 8), 10),
        parseInt(cur.date.slice(8, 10), 10),
        parseInt(cur.date.slice(10, 12), 10),
      );
      dt.setMinutes(dt.getMinutes() + (60 / subdivisions) * j);
      out.push({
        date: dt.toISOString(),
        height: cur.height + (nxt.height - cur.height) * frac,
      });
    }
  }
  // Keep the last original point (bundle: Z.push(G[G.length - 1]))
  out.push(points[points.length - 1]);
  return out;
}

/**
 * cumulusColumns — filter timesteps where pblh > zsfclcl (cumulus condition).
 *
 * Bundle: `N[G].height > Ut[G].height` where N = pblh series, Ut = zsfclcl series.
 * Here we check the raw data field directly (bundle line 1449).
 *
 * @param {Array<{pblh: number, zsfclcl: number, cu_oktas: number, accrain: number, cape: number}>} data
 * @returns {Array<{index: number, cu_oktas: number, accrain: number, cape: number}>}
 */
export function cumulusColumns(data) {
  const cols = [];
  for (let i = 0; i < data.length; i++) {
    const d = data[i];
    if (d.pblh > d.zsfclcl) {
      cols.push({
        index: i,
        cu_oktas: d.cu_oktas ?? 0,
        accrain: d.accrain ?? 0,
        cape: d.cape ?? 0,
      });
    }
  }
  return cols;
}

// ─── Internal series builder (mirrors bundle `ot`) ───────────────────────────

/**
 * Build raw + interpolated series for a given data field.
 * field 0 = pblh (boundary layer), field 1 = zsfclcl (condensation level).
 */
function buildSeries(data, field, subdivisions = 5) {
  const raw = data.map((d) => ({
    date: d.dt,
    height: field === 0 ? d.pblh : d.zsfclcl,
  }));
  return {
    raw,
    interpolated: interpolateSeries(raw, subdivisions),
  };
}

// ─── d3 appenders ────────────────────────────────────────────────────────────

/**
 * drawBoundaryLine — black 2px CatmullRom line over PBLH.
 * Appended as <path class="graph black_path"> (bundle line 1387-1400).
 *
 * Signature: (sel, data, yScale, width, gt)
 */
export function drawBoundaryLine(sel, data, yScale, width, gt) {
  const { interpolated } = buildSeries(data, 0);
  const pathFn = line()
    .x((d, i) => (width / (interpolated.length - 1)) * i)
    .y((d) => yScale(d.height))
    .curve(curveCatmullRom);

  sel
    .append('path')
    .attr('d', pathFn(interpolated))
    .attr('fill', 'none')
    .attr('stroke', 'black')
    .attr('class', 'graph black_path boundary-layer')
    .attr('stroke-width', 2)
    .attr('transform', `translate(${MARGIN_LEFT},${MARGIN_TOP}) scale(${gt},1)`);
}

/**
 * drawCondensationLine — pink 2px CatmullRom line over ZSFCLCL.
 * Appended as <path class="graph condensation-layer black_path"> (bundle line 1401-1414).
 *
 * Signature: (sel, data, yScale, width, gt)
 */
export function drawCondensationLine(sel, data, yScale, width, gt) {
  const { interpolated } = buildSeries(data, 1);
  const pathFn = line()
    .x((d, i) => (width / (interpolated.length - 1)) * i)
    .y((d) => yScale(d.height))
    .curve(curveCatmullRom);

  sel
    .append('path')
    .attr('d', pathFn(interpolated))
    .attr('fill', 'none')
    .attr('stroke', 'pink')
    .attr('class', 'graph condensation-layer black_path')
    .attr('stroke-width', 2)
    .attr('transform', `translate(${MARGIN_LEFT},${MARGIN_TOP}) scale(${gt},1)`);
}

/**
 * drawCloudArea — rgba(70,70,70,0.4) filled area between condensation and boundary.
 * The area y0 = condensation (zsfclcl) height, y1 = min(condensation, boundary) height.
 * (Bundle line 1415-1439: Jt uses vt=interpolated-boundary for x, fe=interpolated-condensation data.)
 *
 * Signature: (sel, data, yScale, width, gt)
 */
export function drawCloudArea(sel, data, yScale, width, gt) {
  const { interpolated: intBoundary } = buildSeries(data, 0);
  const { interpolated: intCondensation } = buildSeries(data, 1);

  // Bundle (1415-1426): area bound to the CONDENSATION series; y0 = BOUNDARY
  // height, y1 = min(condensation, boundary). Fills the grey cloud layer between
  // the boundary line (top) and the condensation level (bottom) wherever
  // condensation < boundary. (Earlier this used condensation for BOTH y0 and y1,
  // collapsing the area to zero height — that's the missing grey band under the BL.)
  const areaFn = area()
    .x((d, i) => (width / (intCondensation.length - 1)) * i)
    .y0((d, i) => yScale(intBoundary[i]?.height ?? d.height))
    .y1((d, i) => yScale(Math.min(d.height, intBoundary[i]?.height ?? d.height)))
    .curve(curveCatmullRom);

  sel
    .append('path')
    .attr('class', 'black_path cloud-area')
    .attr('d', areaFn(intCondensation))
    .attr('fill', 'rgba(70,70,70,0.4)')
    .attr('stroke', 'none')
    .attr('transform', `translate(${MARGIN_LEFT},${MARGIN_TOP}) scale(${gt},1)`);
}
